import { createSelector } from 'reselect';

import { AppState } from '../../store/store';
import { ArticleListItem } from '../models/article-list-item';
import { ArticlesState } from './articles-reducer';
import { currencySelector, localesSelector } from './configuration-selectors';
import { mapArticleDetail } from '../models/mapper/article-detail-mapper';
import { mapArticleListItem } from '../models/mapper/article-list-item-mapper';

const articlesState = (state: AppState): ArticlesState => state.articles;

export const articleSpecialOffersSelector = createSelector(
    currencySelector,
    localesSelector,
    articlesState,
    (currency, locales, state) => {
        return {
            articleOffers: state.articleSpecialOffers.items.map<ArticleListItem>(article =>
                mapArticleListItem(locales, currency, article)
            ),
            total: state.articleSpecialOffers.total,
        };
    }
);

export const articleSearchResultsSelector = createSelector(
    currencySelector,
    localesSelector,
    articlesState,
    (currency, locales, state) => ({
        searchResults: state.articleSearchResults.items.map<ArticleListItem>(article =>
            mapArticleListItem(locales, currency, article)
        ),
        total: state.articleSearchResults.total,
        page: state.articleSearchResults.page,
        pages: state.articleSearchResults.pages,
        categoryCounts: state.articleSearchResults.meta?.categories ?? [],
        suppliersFromSearch: state.articleSearchResults.meta?.suppliers ?? [],
        attributesFromSearch: state.articleSearchResults.meta?.attributes,
        searchEventId: state.articleSearchResults.meta?.searchEventId ?? null,
        relatedSearch: state.articleSearchResults.meta?.relatedSearch ?? null,
    })
);

export const initialArticleSearchResultsSelector = createSelector(
    currencySelector,
    localesSelector,
    articlesState,
    (currency, locales, state) => ({
        searchResults: state.initialArticleSearchResults.items.map<ArticleListItem>(article =>
            mapArticleListItem(locales, currency, article)
        ),
        total: state.initialArticleSearchResults.total,
        page: state.initialArticleSearchResults.page,
        pages: state.initialArticleSearchResults.pages,
        categoryCounts: state.initialArticleSearchResults.meta?.categories ?? [],
        suppliersFromSearch: state.initialArticleSearchResults.meta?.suppliers ?? [],
        attributesFromSearch: state.initialArticleSearchResults.meta?.attributes,
    })
);

export const articleDetailSelector = createSelector(
    currencySelector,
    localesSelector,
    articlesState,
    (currency, locales, state) => {
        if (!state.articleDetail) {
            return null;
        }
        return mapArticleDetail(locales, currency, state.articleDetail);
    }
);

export const createCategorySelector =
    (categoryCounts: Array<{ id: number; parentId?: number; name: string; count: number }>) => (currentId: number) => {
        const currentCategory = categoryCounts.find(category => category.id === currentId);
        const parentCategory = categoryCounts.find(category => category.id === currentCategory?.parentId);
        const subCategories = categoryCounts.filter(
            category => category !== currentCategory && category !== parentCategory
        );

        return {
            parentCategory,
            currentCategory,
            subCategories,
        };
    };

export const articleSearchResultsCategorySelector = createSelector(articleSearchResultsSelector, searchResults =>
    createCategorySelector(searchResults.categoryCounts)
);
